import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import SearchableDropdown from '../common/SearchableDropdown';

const HouseModal = ({ house = null, onClose, onSuccess }) => {
    const { get, post, put } = useApi();
    const { showToast } = useAppState();
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        color: '',
        motto: '',
        house_teacher: ''
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const teacherData = await get('/api/admin/teachers/');
                setTeachers(teacherData);
                if (house) {
                    setFormData({
                        name: house.name,
                        color: house.color || '',
                        motto: house.motto || '',
                        house_teacher: house.house_teacher || ''
                    });
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [get, house]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const dataToSave = {
                ...formData,
                house_teacher: formData.house_teacher || null
            };
            
            if (house) {
                await put(`/api/houses/${house.id}/`, dataToSave);
            } else {
                await post('/api/houses/', dataToSave);
            }
            onSuccess?.();
            showToast(house ? 'House updated' : 'House established successfully', 'success');
            onClose();
        } catch (error) {
            console.error('Error saving house:', error);
            showToast('Could not save house protocol', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading && !house) return null;

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}></div>
            <div className="relative bg-white w-full max-w-xl rounded-[3rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 flex flex-col max-h-[90vh]">
                <div className="p-10 border-b border-slate-50 flex-shrink-0 text-center">
                    <h2 className="text-2xl font-black text-[#18216D] italic uppercase tracking-tighter">
                        {house ? 'Edit House Protocol' : 'Establish New House'}
                    </h2>
                    <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mt-1">Institutional Identity Settings</p>
                </div>
                <form onSubmit={handleSubmit} className="p-10 space-y-6 overflow-y-auto custom-scrollbar">
                    <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">House Name</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            className="w-full bg-white border-2 border-slate-200 focus:border-indigo-500 rounded-2xl px-6 py-4 text-sm font-bold text-[#18216D] transition-all outline-none"
                            placeholder="e.g., Chani"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">House Colour</label>
                        <div className="flex gap-4 items-center">
                            <input
                                type="text"
                                value={formData.color}
                                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                className="flex-1 bg-white border-2 border-slate-200 focus:border-indigo-500 rounded-2xl px-6 py-4 text-sm font-bold text-[#18216D] transition-all outline-none"
                                placeholder="e.g., Yellow or #FFCC00"
                            />
                            <div 
                                className="w-14 h-14 rounded-2xl border-4 border-white shadow-sm"
                                style={{ backgroundColor: formData.color || '#F1F5F9' }}
                            ></div>
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Motto</label>
                        <input
                            type="text"
                            value={formData.motto}
                            onChange={(e) => setFormData({ ...formData, motto: e.target.value })}
                            className="w-full bg-white border-2 border-slate-200 focus:border-indigo-500 rounded-2xl px-6 py-4 text-sm font-bold text-[#18216D] transition-all outline-none"
                            placeholder="e.g., Aim Higher"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">House Teacher / Patron</label>
                        <SearchableDropdown
                            options={teachers}
                            selectedValues={formData.house_teacher ? [formData.house_teacher] : []}
                            onChange={(id) => setFormData({ ...formData, house_teacher: id })}
                            placeholder="Select Faculty Member"
                            isMultiple={false}
                            displayKey="name"
                            className="w-full"
                        />
                    </div>
                    <div className="pt-6 flex gap-4">
                        <button type="button" onClick={onClose} disabled={saving} className="flex-1 py-4 bg-slate-50 text-slate-400 rounded-2xl text-[10px] font-black uppercase tracking-widest disabled:opacity-50">Cancel</button>
                        <button 
                            type="submit" 
                            disabled={saving}
                            className="flex-1 py-4 bg-[#18216D] text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-indigo-900/10 hover:bg-[#111854] transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                            {saving ? (
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    <span>SAVING...</span>
                                </div>
                            ) : 'SAVE CHANGES'}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default HouseModal;
